import { Mark, Slice, Fragment } from '@tiptap/pm/model';
import { closeHistory } from '@tiptap/pm/history';
import { DEFAULT_FONT_SIZE, ORDERED } from '../../shared/paste/htmlPaste';

/**
 * Collage mis en forme dans un texte TipTap du site, traduction des
 * paragraphes de `htmlToFormattedBlocks` (shared/paste) en contenu
 * ProseMirror — le pendant de `buildFragment` du classique.
 */

// Ce qu'un collage porte lui-même : il remplace ces marks du curseur.
const OWN_MARKS = new Set(['bold', 'italic', 'underline', 'highlight']);

/* Copie interne entre deux textes du site : ProseMirror la recolle telle
   quelle, variables `@` comprises, ce que notre lecture du HTML perdrait. */
export const isEditorClipboard = (html) => /data-pm-slice|data-type="proposal-variable"/.test(html || '');

/**
 * Marks d'un morceau collé. Sans `format` (« Conserver uniquement le texte ») :
 * celles du curseur. Sinon les marks du curseur restent (police, taille), sauf
 * celles que le collage porte : gras, italique, souligné, couleur, surlignage,
 * et la taille quand il a la sienne.
 */
function runMarks(schema, format, cursorMarks, palette, toFontSize) {
  if (!format) return cursorMarks;
  const cursorStyle = cursorMarks.find((mark) => mark.type.name === 'textStyle')?.attrs || {};
  const marks = cursorMarks.filter((mark) => !OWN_MARKS.has(mark.type.name) && mark.type.name !== 'textStyle');
  if (format.bold) marks.push(schema.marks.bold.create());
  if (format.italic) marks.push(schema.marks.italic.create());
  if (format.underline) marks.push(schema.marks.underline.create());

  const size = palette.fontSize(format.fontSize);
  const textStyle = {
    ...cursorStyle,
    color: palette.color(format.color),
    fontSize: size === DEFAULT_FONT_SIZE ? null : (size && toFontSize(size)) || cursorStyle.fontSize || null,
  };
  if (Object.values(textStyle).some((value) => value != null)) marks.push(schema.marks.textStyle.create(textStyle));

  const highlight = palette.highlight(format.highlight, format.highlightMarked);
  if (highlight) marks.push(schema.marks.highlight.create({ color: highlight }));
  return Mark.setFrom(marks);
}

// Un nœud texte par morceau de même format.
function textNodes(schema, { text, formats }, marksOf) {
  const nodes = [];
  let start = 0;
  for (let i = 1; i <= text.length; i += 1) {
    if (i === text.length || formats[i] !== formats[start]) {
      nodes.push(schema.text(text.slice(start, i), marksOf(formats[start])));
      start = i;
    }
  }
  return nodes;
}

const LIST_NODES = { [ORDERED]: 'orderedList' };

// Paragraphes et éléments de liste à plat (type, niveau) en listes imbriquées.
function blockNodes(schema, blocks, marksOf) {
  const roots = [];
  // Listes ouvertes, de la plus externe à la plus interne.
  let open = [];
  blocks.forEach((block) => {
    const paragraph = schema.nodes.paragraph.create(null, textNodes(schema, block, marksOf));
    if (!block.list) {
      open = [];
      roots.push(paragraph);
      return;
    }
    const { depth } = block.list;
    const listType = LIST_NODES[block.list.type] || 'bulletList';
    while (open.length && (open[open.length - 1].depth > depth
      || (open[open.length - 1].depth === depth && open[open.length - 1].type !== listType))) {
      open.pop();
    }
    const parent = open[open.length - 1];
    if (!parent || parent.depth < depth) {
      const list = { type: listType, depth, items: [] };
      if (parent) parent.items[parent.items.length - 1].push(list);
      else roots.push(list);
      open.push(list);
    }
    open[open.length - 1].items.push([paragraph]);
  });

  const toNode = (entry) => (entry.items
    ? schema.nodes[entry.type].create(null, entry.items.map((children) => (
      schema.nodes.listItem.create(null, children.map(toNode))
    )))
    : entry);
  return roots.map(toNode);
}

// Profondeur jusqu'au premier (ou dernier) bloc de texte : le collage s'y
// fond dans le paragraphe du curseur, comme un collage ProseMirror.
const openDepth = (node, edge) => {
  let depth = 0;
  let current = node;
  while (current && !current.isTextblock) {
    current = current[edge];
    depth += 1;
  }
  return depth + 1;
};

/**
 * Le contenu à coller pour des paragraphes formatés.
 *
 * @param {object} options
 * @param {boolean} options.textOnly - sans les formats collés
 * @param {object} options.palette - `pastePalette` (shared/paste)
 * @param {Function} options.toFontSize - taille en px vers sa valeur CSS
 */
export function pasteSlice(state, blocks, { textOnly, palette, toFontSize }) {
  const { schema } = state;
  const cursorMarks = state.storedMarks || state.selection.$from.marks();
  const marksOf = (format) => runMarks(schema, textOnly ? null : format, cursorMarks, palette, toFontSize);
  const nodes = blockNodes(schema, blocks, marksOf);
  return new Slice(
    Fragment.fromArray(nodes),
    openDepth(nodes[0], 'firstChild'),
    openDepth(nodes[nodes.length - 1], 'lastChild'),
  );
}

/** Colle `slice` à la place de la sélection, en une étape d'annulation à part. */
export function dispatchPaste(view, slice) {
  const tr = view.state.tr.replaceSelection(slice).scrollIntoView();
  tr.setMeta('paste', true);
  tr.setMeta('uiEvent', 'paste');
  view.dispatch(closeHistory(tr));
}
